import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { CalendarDays } from 'lucide-react';
import 'react-day-picker/dist/style.css';
import { cn } from '@/lib/utils';
import { MenuPanel } from './menu-panel';

// 'YYYY-MM-DD' <-> local Date, avoiding the UTC shift of new Date('YYYY-MM-DD')
function toDate(v?: string): Date | undefined {
  if (!v) return undefined;
  const [y, m, d] = v.split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : undefined;
}

function toValue(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function toDisplay(v?: string): string {
  const d = toDate(v);
  if (!d) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

interface DatePickerProps {
  /** 'YYYY-MM-DD' — same contract as a native <input type="date"> */
  value?: string;
  onChange: (v: string) => void;
  /** Fired when the picker closes — lets react-hook-form mark the field touched */
  onBlur?: () => void;
  /** Inclusive bounds ('YYYY-MM-DD'); days outside are not selectable */
  min?: string;
  max?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Which edge the calendar panel anchors to (default 'start' = left) */
  align?: 'start' | 'end';
}

export function DatePicker({
  value,
  onChange,
  onBlur,
  min,
  max,
  placeholder = 'DD/MM/YYYY',
  disabled,
  className,
  align = 'start',
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const close = () => {
    setOpen(false);
    onBlur?.();
  };

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('pointerdown', onOutside);
    return () => document.removeEventListener('pointerdown', onOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const minDate = toDate(min);
  const maxDate = toDate(max);
  const disabledDays = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];

  return (
    // w-full: the trigger must fill its slot, not shrink-wrap the text —
    // otherwise the width jumps between placeholder and a selected date
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-xl border border-smoke bg-neutral-white px-3 text-base outline-none focus:border-primary-s500 disabled:cursor-not-allowed disabled:opacity-50',
          value ? 'text-black' : 'text-lightGray',
          className,
        )}
      >
        <span className="truncate">{toDisplay(value) || placeholder}</span>
        <CalendarDays size={16} className="shrink-0 text-silver" />
      </button>
      {open && (
        <MenuPanel
          className={cn(
            'absolute top-full z-50 mt-1 w-auto shadow-md',
            align === 'end' ? 'right-0' : 'left-0',
          )}
        >
          <DayPicker
            mode="single"
            selected={toDate(value)}
            defaultMonth={toDate(value) ?? minDate}
            disabled={disabledDays.length > 0 ? disabledDays : undefined}
            onSelect={(d) => {
              onChange(d ? toValue(d) : '');
              close();
            }}
            style={{ '--rdp-accent-color': '#1565C0' } as React.CSSProperties}
          />
        </MenuPanel>
      )}
    </div>
  );
}
